import { Box, Stack, useTheme } from '@mui/material'
import { LoadingSkeleton, TextWithEllipsis, TinyButton, tokens, TOOLBAR_HEIGHT } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'

import { isEmpty } from 'lodash'
import { BORDER_WIDTH, THREAD_HEADER_HEIGHT } from 'ComponentLibrary/src/widgets/ThreadSelection/constants'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { PlusCircleIcon, PlusIcon } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { ReactNode, RefObject } from 'react'
import {
    DEFAULT_THREAD_SELECTION_ITEM_COPY,
    Thread,
    ThreadSelectionItem,
    type ThreadSelectionItemCopy,
    UseDeleteThreadType,
} from './ThreadSelectionItem'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ThreadSelectionProps<TaskType> = {
    title?: string
    subtitle?: string
    threads: Thread<TaskType>[] | undefined
    activeThreadId: UUID | undefined
    onClickThread: (index: string) => void
    createMode: boolean
    setCreateMode: (bool: boolean) => void
    footer?: ReactNode
    isLoadingThreads?: boolean
    isLoadingOrderData?: boolean
    filterConfig?: FilterConfig
    threadListRef?: RefObject<HTMLDivElement | null>
    pinThread: (threadId: string) => void
    unpinThread: (threadId: string) => void
    useDeleteThread: UseDeleteThreadType
    customerId?: string
    copy?: Partial<ThreadSelectionCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

type FilterConfig = {
    filters: ReactNode
    disableCreate?: boolean
    disableCreateText?: string
    isAnyFilterActive: boolean
    noResultsText: string
}

const THREAD_SELECTION_SIDEBAR_WIDTH = '480px'
const THREAD_SELECTION_SIDEBAR_WIDTH_WITH_BORDER = `calc(${THREAD_SELECTION_SIDEBAR_WIDTH} + ${BORDER_WIDTH})`

export type ThreadSelectionCopy = {
    newThread: string
    item: ThreadSelectionItemCopy
}

const DEFAULT_THREAD_SELECTION_COPY: ThreadSelectionCopy = {
    newThread: 'Neue Unterhaltung',
    item: DEFAULT_THREAD_SELECTION_ITEM_COPY,
}

export function ThreadSelection<TaskType>({
    threads,
    title,
    subtitle,
    activeThreadId,
    onClickThread,
    createMode,
    setCreateMode,
    footer,
    isLoadingThreads = false,
    isLoadingOrderData = false,
    filterConfig,
    threadListRef,
    pinThread,
    unpinThread,
    useDeleteThread,
    customerId,
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: ThreadSelectionProps<TaskType>) {
    const theme = useTheme()
    const resolvedCopy = { ...DEFAULT_THREAD_SELECTION_COPY, ...copy }
    const itemCopy = { ...DEFAULT_THREAD_SELECTION_COPY.item, ...copy?.item }
    return (
        <Stack
            width={THREAD_SELECTION_SIDEBAR_WIDTH_WITH_BORDER}
            borderRight={`${BORDER_WIDTH} solid ${theme.palette.secondary1.grey}`}
            height={`calc(100vh - ${TOOLBAR_HEIGHT} - ${BORDER_WIDTH})`}
        >
            <Stack
                minHeight={THREAD_HEADER_HEIGHT}
                direction="row"
                justifyContent="space-between"
                paddingX={tokens.SizingSpacing200}
                alignItems="center"
                borderBottom={`${BORDER_WIDTH} solid ${theme.palette.secondary1.grey}`}
                position="sticky"
                top="0px"
            >
                <Stack direction="column" minWidth={0}>
                    {isLoadingThreads && !title && !subtitle && <LoadingSkeleton height="36px" width="320px" />}
                    {title && (
                        <TextWithEllipsis typographyProps={{ variant: 'headline1', color: tokens.ColorTextBoldest }} tooltipText={title}>
                            {title}
                        </TextWithEllipsis>
                    )}
                    {subtitle && (
                        <TextWithEllipsis
                            typographyProps={{ variant: 'bodyTextMedium', color: tokens.ColorTextSubtle }}
                            tooltipText={subtitle}
                        >
                            {subtitle}
                        </TextWithEllipsis>
                    )}
                </Stack>
                <IconButton
                    icon={<PlusCircleIcon />}
                    variant="tertiary"
                    size="medium"
                    tooltip={resolvedCopy.newThread}
                    onClick={() => {
                        setCreateMode(true)
                    }}
                    disabled={filterConfig?.disableCreate}
                />
            </Stack>
            {filterConfig !== undefined && (
                <Stack
                    padding={`${tokens.SizingSpacing150} ${tokens.SizingSpacing300}`}
                    gap={tokens.SizingSpacing150}
                    justifyContent="center"
                    direction="row"
                >
                    {filterConfig.filters}
                </Stack>
            )}
            {isEmpty(threads) && !isLoadingThreads && (
                <Stack
                    alignItems="center"
                    direction="column"
                    padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`}
                    gap={tokens.SizingSpacing300}
                >
                    {filterConfig?.disableCreate && (
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {filterConfig.disableCreateText}
                        </Typography>
                    )}
                    {filterConfig?.isAnyFilterActive && (
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {filterConfig.noResultsText}
                        </Typography>
                    )}
                    {!filterConfig?.disableCreate && (
                        <TinyButton leftIcon={<PlusIcon />} label={resolvedCopy.newThread} onClick={() => setCreateMode(true)} />
                    )}
                </Stack>
            )}
            <Stack
                direction="column"
                height="100%"
                overflow="auto"
                position="relative"
                justifyContent="space-between"
                width={THREAD_SELECTION_SIDEBAR_WIDTH}
                flexGrow={1}
            >
                <Stack direction="column" ref={threadListRef}>
                    {isLoadingThreads && (
                        <LoadingSkeleton
                            count={5}
                            height="72.5px"
                            sx={{ transform: 'none', margin: `${tokens.SizingSpacing100} ${tokens.SizingSpacing200}` }}
                        />
                    )}
                    {!isEmpty(threads) &&
                        !isLoadingThreads &&
                        threads?.map((thread) => (
                            <ThreadSelectionItem
                                key={thread.uuid}
                                thread={thread}
                                createMode={createMode}
                                activeThreadId={activeThreadId}
                                onClickThread={onClickThread}
                                pinThread={pinThread}
                                unpinThread={unpinThread}
                                isLoadingOrderData={isLoadingOrderData}
                                useDeleteThread={useDeleteThread}
                                customerId={customerId}
                                copy={itemCopy}
                                userLinkCopy={userLinkCopy}
                                locale={locale}
                            />
                        ))}
                </Stack>
                {footer && (
                    <Box
                        position="sticky"
                        bottom={0}
                        sx={{ backgroundColor: tokens.ColorBackgroundLight }}
                        width={'100%'}
                        borderTop={`${BORDER_WIDTH} solid ${theme.palette.secondary1.grey}`}
                    >
                        {footer}
                    </Box>
                )}
            </Stack>
        </Stack>
    )
}
